import { makeAutoObservable, reaction, type IReactionDisposer, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import { showToast } from 'shared/ui'
import { TableStore } from 'shared/ui/Table'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import { IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import {
  type ICrmUser,
  type ICrmUserIntegrationMeta,
  type Integration,
  IntegrationKey,
  IntegrationsApi,
  type IParamsPagination,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const defaultPagination = {
  page: 1,
  limit: 10,
}

export class IntegrationSettingsUsersStore {
  private _isLoading = false
  private _disposeChangePagination: IReactionDisposer | null = null
  private _disposeSearch: IReactionDisposer | null = null
  private _userCount = 0
  private _mappedUsersCount = 0
  private _total = 0
  private _paginationData = defaultPagination
  private _search = ''
  private _scrollToTOpTrigger = ''
  private _users = new Map<number, IntegrationCrmUser<ICrmUserIntegrationMeta>>()
  private _tableStore = new TableStore<IntegrationCrmUser<ICrmUserIntegrationMeta>>({
    withoutDefaultManageColumns: true,
  })
  private _hideAutoMapUsersAlert = true

  constructor(private _integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    this.getUsersData()
    this._reactionChange()
    this._entities.usersStore.initUsersOrganization()

    await this._entities.usersStore.initPromise
    this._hideAutoMapUsersAlert = localStorage.getItem(this.hideAutoMapUsersAlertKey) === 'true'
  }

  private _reactionChange = () => {
    this._disposeChangePagination?.()
    this._disposeSearch?.()
    this._disposeChangePagination = reaction(
      () => this._paginationData,
      () => this.getUsersData()
    )
    this._disposeSearch = reaction(
      () => this.search,
      () =>
        this._requestLoadUsers.debounceLoadData({
          page: 1,
          limit: this._paginationData.limit,
          query: this._search,
        })
    )
  }

  dispose = () => {
    this._paginationData = defaultPagination
    this._total = 0
    this._disposeChangePagination?.()
    this._disposeSearch?.()
  }

  get isCalendlyIntegration() {
    return this._integration.key === IntegrationKey.calendly
  }

  get usesIntegrationCalendarUserMapping() {
    return (
      this._integration.key === IntegrationKey.calendly ||
      this._integration.key === IntegrationKey.google_calendar
    )
  }

  get hideAutoMapUsersAlertKey() {
    const userId = this._entities.usersStore.user?.id || 0

    return `${userId}_${this._integration.key}_hideAutoMapUsersAlert`
  }

  get canAutoMapUsers() {
    if (this._hideAutoMapUsersAlert) return false

    const canAutoMapUsers = [
      IntegrationKey.hubspot,
      IntegrationKey.salesforce,
      IntegrationKey.infusionsoft,
      IntegrationKey.activecampaign,
      IntegrationKey.calendly,
    ]

    return canAutoMapUsers.includes(this._integration.key as IntegrationKey)
  }

  private _requestLoadUsers = new RequestLoadData<IParamsPagination>({
    requestCallback: async (
      { cancelRequest },
      params = {
        ...this._paginationData,
        query: this._search,
      }
    ) => {
      if (!this.search) {
        delete params.query
      }

      const { data } = this.usesIntegrationCalendarUserMapping
        ? await IntegrationsApi.getIntegrationCalendarUsersList(this._integration.key, params, {
            cancelToken: cancelRequest.token,
          })
        : await IntegrationsApi.getIntegrationUsersList(this._integration.key, params, {
            cancelToken: cancelRequest.token,
          })

      runInAction(() => {
        this._users.clear()
        this._total = data.meta.total || data.total
        this._mappedUsersCount = data.meta.mapped
        this._userCount = data.total || data.meta.total || data.meta.total_items
        data.data.forEach((user) => {
          this.setUser(user)
        })
        this._scrollToTOpTrigger = nanoid()
      })
    },
  })

  getUsersData = (
    params: IParamsPagination = {
      ...this._paginationData,
      query: this._search,
    }
  ) => {
    this._requestLoadUsers.loadData(params)
  }

  private _requestUpdateUsers = new RequestLoadData<{
    userId: number
    memberId: number
    wasMapped: boolean
  }>({
    requestCallback: async (_config, params) => {
      if (!params) return

      const { userId, memberId, wasMapped } = params

      const { data } = this.usesIntegrationCalendarUserMapping
        ? await IntegrationsApi.putIntegrationCalendarUserFieldValue(
            this._integration.key,
            userId,
            {
              mapped_user_id: memberId,
            }
          )
        : await IntegrationsApi.patchIntegrationUserFieldValue(this._integration.key, userId, {
            attribute: 'user_id',
            value: memberId,
          })

      runInAction(() => {
        this.setUser(data)
        if (wasMapped && !(data.user_id || data.mapped_user_id)) {
          this._mappedUsersCount -= 1
        }
        if (!wasMapped && (data.user_id || data.mapped_user_id)) {
          this._mappedUsersCount += 1
        }
      })
      showToast({
        type: 'success',
        title: data.user_id || data.mapped_user_id ? 'Account Linked' : 'Account Unlinked',
      })
    },
  })

  updateUserBinding = (userId: number, memberId: number, wasMapped: boolean) => {
    this._requestUpdateUsers.loadData({
      userId,
      memberId,
      wasMapped,
    })
  }

  setUser(user: ICrmUser<ICrmUserIntegrationMeta>) {
    this._users.set(user.id, new IntegrationCrmUser<ICrmUserIntegrationMeta>(user))
  }

  sendRefreshRequest = async () => {
    this._isLoading = true

    try {
      this.usesIntegrationCalendarUserMapping
        ? await IntegrationsApi.activateIntegrationCalendarRefresh(this._integration.key)
        : await IntegrationsApi.activateIntegrationRefresh(this._integration.key)
      this.refreshData()
    } catch (e) {
      logger.error(e)
    } finally {
      this._isLoading = false
    }
  }

  refreshData = async () => {
    await Promise.all([this._entities.usersStore.initUsersOrganization(), this.getUsersData()])
    this._isLoading = false
    showToast({
      type: 'success',
      title: 'Users refreshed',
    })
  }

  hideAutoMapUsersAlert = async () => {
    this._hideAutoMapUsersAlert = true
    localStorage.setItem(this.hideAutoMapUsersAlertKey, 'true')
  }

  autoMapUsers = async () => {
    if (this._isLoading) return
    this._isLoading = true

    try {
      const { data } = this.isCalendlyIntegration
        ? await IntegrationsApi.activateCalendlyIntegrationAutoMappingUsers()
        : await IntegrationsApi.autoMapIntegrationUsers(this._integration.key)
      showToast({
        type: 'success',
        title: data.message,
      })
      this.refreshData()
      this.hideAutoMapUsersAlert()
    } catch (e) {
      this._isLoading = false
      logger.error(e)

      if (e instanceof AxiosError) {
        const message = e.errors.getError('message')
        if (message) {
          showToast({
            type: 'error',
            title: message,
          })
        }
      }
    }
  }

  onChangePagination = (page: number, limit: number) => {
    this._paginationData = {
      page,
      limit,
    }
  }

  onSearchChange = (search: string) => {
    this._search = search
  }

  onUserChange = (userId: number, memberId: number, wasMapped: boolean) => {
    const row = this._users.get(userId)
    if (this._tableStore.selectedIds.includes(userId) && row) {
      this._tableStore.toggleSelected(row, false)
    }

    this.updateUserBinding(userId, memberId, wasMapped)
  }

  private _onInvite = (userIds: number[]) => {
    uiStore.changeRouteOnlyVue('push', {
      name: 'settings.organization.members.invite',
      params: {
        preselect: JSON.stringify({
          userIds: userIds,
          integrationKey: this._integration.key,
        }),
        tab: 'active',
      },
    })
  }

  onGroupInvite = () => {
    this._onInvite(this._tableStore.selectedIds as number[])
  }

  onUserInvite = (userId: number) => {
    this._onInvite([userId])
  }

  onManageInvite = (userId: number) => {
    uiStore.changeRouteViaVue({
      path: '/settings/organization/members/pending',
      query: `?uid=${userId}`,
    })
  }

  get search() {
    return this._search
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get users() {
    return Array.from(this._users.values())
  }

  get organizationMembers() {
    return this._entities.usersStore.users.filter((user) => Boolean(user.email))
  }

  get status() {
    return this._requestLoadUsers.status
  }

  get total() {
    return this._total
  }

  get usersCount() {
    return this._userCount
  }

  get mappedUsersCount() {
    return this._mappedUsersCount
  }

  get integrationName() {
    return this._integration?.name || ''
  }

  get userInfo() {
    return this.usersCount
      ? {
          subtitle: `(${this.mappedUsersCount} / ${this.usersCount})`,
          label: `There are ${this.usersCount - this.mappedUsersCount} more ${
            this._integration.name
          } users that don’t have a Salesmsg account`,
        }
      : {
          subtitle: '(0)',
          label: `There are no ${this._integration.name} users`,
        }
  }

  get pagination() {
    return this._paginationData
  }

  get tableStore() {
    return this._tableStore
  }

  get isLoading() {
    return this._isLoading || this._requestLoadUsers.loading
  }
}
